import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LocaleFlag } from "@/components/common/LocaleFlag";
import { cn } from "@/lib/utils";
import { useLocale, useTranslate, SUPPORTED_LOCALES, type Locale } from "@/i18n";

interface LocaleSwitcherProps {
  /** Optional class override for the trigger button. */
  className?: string;
  /** Visual density. `"compact"` matches the compact header density. */
  density?: "default" | "compact";
}

const LABEL_KEYS: Record<Locale, string> = {
  sv: "locale.sv",
  en: "locale.en",
};

/**
 * Dropdown locale switcher for public surfaces.
 *
 * Trigger is a small flag pill — keeps the public header calm and editorial
 * (no SV | EN text crowding the nav). The menu lists every supported locale
 * with its flag and native name, with a check on the active one.
 *
 * Intentionally not used inside `/app/*` portals — admin/producer/editor/
 * customer surfaces stay Swedish per v1 (see `docs/SoT/localization.md`).
 */
export function LocaleSwitcher({ className, density = "default" }: LocaleSwitcherProps) {
  const { locale, setLocale } = useLocale();
  const t = useTranslate();

  const triggerSize = density === "compact" ? "h-7 px-2" : "h-8 px-2.5";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={t("locale.switch.label")}
          data-testid="locale-switch-trigger"
          className={cn(
            triggerSize,
            "rounded-full text-muted-foreground hover:text-foreground",
            "gap-1.5",
            className,
          )}
        >
          <LocaleFlag locale={locale} />
          <span className="text-xs font-medium uppercase tracking-wide">
            {locale === "sv" ? "SV" : "EN"}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[10rem]">
        {SUPPORTED_LOCALES.map((value) => {
          const isActive = value === locale;
          return (
            <DropdownMenuItem
              key={value}
              onSelect={() => setLocale(value)}
              data-testid={`locale-switch-${value}`}
              aria-current={isActive ? "true" : undefined}
              className="flex items-center gap-2.5"
            >
              <LocaleFlag locale={value} />
              <span className="flex-1 text-sm">{t(LABEL_KEYS[value])}</span>
              {isActive && <Check className="h-3.5 w-3.5 text-foreground" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LocaleSwitcher;
